import React, { createContext, useState, useEffect, useContext } from 'react';
import { logAuthFailure } from '../services/PreAuthLogService';
import {
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updatePassword as firebaseUpdatePassword,
  sendEmailVerification
} from 'firebase/auth';
import { auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import UserService from '../services/UserService';
import { setCurrentUserId } from '../utils/userStorage';
import { preUnlockContentFromDrumYears } from '../utils/parcoursProgressionStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isPendingEmailVerification, setIsPendingEmailVerification] = useState(false);

  // Mode "Jouer avec élève" pour les professeurs
  // Initialiser depuis sessionStorage pour persister au rechargement (ADR-020)
  const [teacherMode, setTeacherMode] = useState(() => {
    try {
      const saved = sessionStorage.getItem('teacherMode');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed;
      }
    } catch (error) {
      console.error('Erreur lecture sessionStorage teacherMode:', error);
    }
    return {
      isActive: false,
      originalTeacherId: null,
      originalTeacherProfile: null,
      studentId: null, // UID de l'élève
      studentProfile: null
    };
  });

  // Connexion avec email/password
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Forcer le rechargement du token pour récupérer l'état de vérification à jour
      // (cas où l'email a été vérifié dans un autre navigateur/appareil)
      try {
        await userCredential.user.reload();
      } catch (reloadError) {
        console.warn('⚠️ reload() a échoué:', reloadError);
      }
      const freshUser = auth.currentUser;
      if (!freshUser?.emailVerified) {
        // Déconnecter immédiatement pour éviter l'accès sans vérification
        await signOut(auth);
        const error = new Error('Email non vérifié');
        error.code = 'auth/email-not-verified';
        throw error;
      }
      return userCredential;
    } catch (error) {
      console.error('❌ Erreur de connexion:', error);
      logAuthFailure('login_context', error);
      throw error;
    }
  };

  // Recharger l'état de vérification email (appelé après clic sur lien ou polling)
  const refreshEmailVerification = async () => {
    if (!auth.currentUser) return false;
    try {
      await auth.currentUser.reload();
    } catch (e) {
      console.warn('⚠️ reload() a échoué:', e);
      return false;
    }
    if (auth.currentUser.emailVerified) {
      // Le jeton d'identité porte email_verified : le rafraîchir pour que les règles Firestore le voient.
      await auth.currentUser.getIdToken(true);
      setIsPendingEmailVerification(false);
      setCurrentUser(auth.currentUser);
      await loadUserProfile(auth.currentUser.uid);
      return true;
    }
    return false;
  };

  // Renvoyer l'email de vérification (utilisateur non connecté)
  const resendVerificationEmail = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      await signOut(auth);
    } catch (error) {
      console.error('❌ Erreur renvoi email:', error);
      logAuthFailure('verification_resend', error);
      throw error;
    }
  };

  // Connexion anonyme (juste avec pseudo)
  const loginAnonymously = async () => {
    try {
      const userCredential = await signInAnonymously(auth);
      return userCredential;
    } catch (error) {
      console.error('❌ Erreur de connexion anonyme:', error);
      logAuthFailure('login_anonymous', error);
      throw error;
    }
  };

  // Déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      setIsFirstLogin(false);
    } catch (error) {
      console.error('❌ Erreur de déconnexion:', error);
      throw error;
    }
  };

  // Créer le profil utilisateur (première connexion)
  const createProfile = async (pseudo, age, drumYears, accountType = 'email') => {
    try {
      if (!currentUser) {
        throw new Error('Aucun utilisateur connecté');
      }

      // Force-refresh le token pour s'assurer que email_verified est à jour dans le JWT
      await currentUser.getIdToken(true);

      const profile = await UserService.createUserProfile(
        currentUser.uid,
        currentUser.email,
        pseudo,
        age,
        drumYears,
        accountType
      );

      setUserProfile(profile);
      setIsFirstLogin(false);
      return profile;
    } catch (error) {
      console.error('❌ Erreur création profil:', error);
      throw error;
    }
  };

  // Mettre à jour le pseudo
  const updatePseudo = async (newPseudo) => {
    try {
      if (!currentUser) {
        throw new Error('Aucun utilisateur connecté');
      }

      await UserService.updatePseudo(currentUser.uid, newPseudo);

      // Mettre à jour le profil local
      setUserProfile(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          pseudo: newPseudo
        }
      }));
    } catch (error) {
      console.error('❌ Erreur mise à jour pseudo:', error);
      throw error;
    }
  };

  // Marquer un badge de challenge (Drill/Lecture) comme vu après affichage de la notif XP
  const markChallengeBadgeSeen = async (challengeId, position) => {
    try {
      if (!currentUser) {
        throw new Error('Aucun utilisateur connecté');
      }

      await UserService.markChallengeBadgeSeen(currentUser.uid, challengeId, position);

      // Mettre à jour le profil local (profil chargé en one-shot, pas de listener temps réel)
      setUserProfile(prev => {
        if (!prev) return prev;
        const badges = prev.profile?.challengeBadges || [];
        return {
          ...prev,
          profile: {
            ...prev.profile,
            challengeBadges: badges.map(b =>
              (b.challengeId === challengeId && b.position === position)
                ? { ...b, seen: true }
                : b
            ),
          },
        };
      });
    } catch (error) {
      console.error('❌ Erreur marquage badge vu:', error);
      throw error;
    }
  };

  // Supprimer le compte (RGPD - droit à l'effacement)
  // Toute la suppression est gérée côté serveur par la Cloud Function deleteAccount
  const deleteAccount = async () => {
    try {
      if (!currentUser) throw new Error('Aucun utilisateur connecté');

      const fn = httpsCallable(functions, 'deleteAccount');
      await fn();

      // Nettoyer le state local
      setCurrentUserId(null);
      localStorage.clear();
      sessionStorage.clear();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('❌ Erreur suppression compte:', error);
      throw error;
    }
  };

  // Changer le mot de passe
  const updatePassword = async (newPassword) => {
    try {
      if (!currentUser) {
        throw new Error('Aucun utilisateur connecté');
      }

      await firebaseUpdatePassword(currentUser, newPassword);
    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      throw error;
    }
  };

  // Charger le profil utilisateur
  const loadUserProfile = async (userId) => {
    try {
      const profile = await UserService.getUserProfile(userId);

      if (profile) {
        // Vérifier si le compte a été révoqué
        if (profile.profile?.isRevoked === true) {
          await signOut(auth);
          setCurrentUser(null);
          setUserProfile(null);
          setIsFirstLogin(false);
          // Stocker le message pour l'afficher sur la page de login
          sessionStorage.setItem('revokedAccount', '1');
          return;
        }

        setUserProfile(profile);
        setIsFirstLogin(false);

        // Pré-débloquer le contenu pour les comptes avec expérience préalable
        // (idempotent grâce au flag contentPreUnlocked en localStorage)
        const drumYears = profile.profile?.drumYears;
        const isTeacher = profile.profile?.role === 'teacher';
        if (drumYears && drumYears > 1 && !isTeacher) {
          preUnlockContentFromDrumYears(drumYears, userId).catch(err => {
            console.warn('[Parcours] Erreur pré-déblocage contenu (login):', err);
          });
        }

        // Mettre à jour la date de dernière connexion — non bloquant :
        // un échec ici (ex: permission-denied) ne doit jamais remonter au catch
        // global ci-dessous, sinon isFirstLogin repasse à tort à true pour un
        // compte existant (incident du 18/09/2026).
        UserService.updateLastLogin(userId).catch((err) => {
          console.warn('[Auth] Échec non bloquant updateLastLogin:', err);
        });
      } else {
        // Pas de profil en Firestore — vérifier si données en attente de l'inscription
        const pendingRaw = localStorage.getItem(`pendingProfile_${userId}`);
        if (pendingRaw) {
          try {
            const pendingData = JSON.parse(pendingRaw);
            // Ancien format (pseudo + drumYears) : auto-créer le profil
            if (pendingData.pseudo && pendingData.drumYears) {
              const profile = await UserService.createUserProfile(
                userId,
                auth.currentUser.email,
                pendingData.pseudo,
                null,
                pendingData.drumYears,
                pendingData.accountType
              );
              localStorage.removeItem(`pendingProfile_${userId}`);
              setUserProfile(profile);
              setIsFirstLogin(false);
            } else {
              // Nouveau format (accountType seulement) : laisser FirstLoginPage gérer
              setIsFirstLogin(true);
            }
          } catch (createError) {
            console.error('❌ Erreur création profil différé:', createError);
            setIsFirstLogin(true);
          }
        } else {
          // Première connexion sans données en attente (ex: Google auth)
          setIsFirstLogin(true);
        }
      }
    } catch (error) {
      console.error('❌ Erreur chargement profil (catch global):', error, { userId, errorCode: error?.code, errorMessage: error?.message });
      setIsFirstLogin(true);
    }
  };

  // Écouter les changements d'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('[Auth] onAuthStateChanged fired', { uid: user?.uid, isAnonymous: user?.isAnonymous });
      setCurrentUser(user);

      if (user) {
        // Email non vérifié : bloquer avant tout chargement de profil
        // Les comptes anonymes n'ont pas d'email — ne pas les bloquer ici
        if (!user.emailVerified && !user.isAnonymous) {
          setCurrentUserId(user.uid);
          setIsPendingEmailVerification(true);
          setLoading(false);
          return;
        }
        setIsPendingEmailVerification(false);

        // Vérifier le mode prof AVANT de définir _currentUserId.
        // Si on fait setCurrentUserId(user.uid) d'abord, puis await loadUserProfile(),
        // _currentUserId reste sur le prof pendant tout l'await → contamination des
        // lectures localStorage/Firestore de l'élève (ex: chapitre 42 au lieu de 10).
        const savedMode = sessionStorage.getItem('teacherMode');
        if (savedMode) {
          try {
            const parsed = JSON.parse(savedMode);
            if (parsed.isActive && parsed.studentProfile && parsed.studentId) {
              // Mode prof actif : cibler directement l'élève sans jamais passer par l'UID prof
              setCurrentUserId(parsed.studentId);
              setUserProfile(parsed.studentProfile);
            } else {
              // Données de session incomplètes : revenir en mode normal
              setCurrentUserId(user.uid);
              await loadUserProfile(user.uid);
            }
          } catch (error) {
            console.error('Erreur restauration mode professeur:', error);
            setCurrentUserId(user.uid);
            await loadUserProfile(user.uid);
          }
        } else {
          // Mode normal (pas de session élève active)
          setCurrentUserId(user.uid);
          await loadUserProfile(user.uid);
        }
      } else {
        setCurrentUserId(null);
        setUserProfile(null);
        setIsFirstLogin(false);
        // Nettoyer sessionStorage à la déconnexion
        sessionStorage.removeItem('teacherMode');
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Activer le mode "Jouer avec élève" (pour les professeurs)
  const playWithStudent = async (studentId) => {
    console.log('[Auth] playWithStudent → start', { teacherId: currentUser?.uid, studentId });
    try {
      // Vérifier que l'utilisateur est bien un professeur
      if (userProfile?.profile?.role !== 'teacher') {
        throw new Error('Seuls les professeurs peuvent utiliser cette fonctionnalité');
      }

      // Charger le profil de l'élève
      const studentData = await UserService.getUserProfile(studentId);

      if (!studentData) {
        throw new Error('Élève introuvable');
      }

      // Créer le nouveau mode professeur
      const newTeacherMode = {
        isActive: true,
        originalTeacherId: currentUser.uid,
        originalTeacherProfile: userProfile,
        studentId: studentId, // ⚠️ CRITIQUE : Sauvegarder l'UID de l'élève
        studentProfile: studentData
      };

      // Sauvegarder dans sessionStorage pour persister au rechargement (ADR-020)
      sessionStorage.setItem('teacherMode', JSON.stringify(newTeacherMode));

      // Basculer le localStorage vers l'élève
      console.log('[Auth] playWithStudent → setCurrentUserId(studentId)');
      setCurrentUserId(studentId);

      // Sauvegarder le profil actuel du professeur + l'UID de l'élève
      console.log('[Auth] playWithStudent → setTeacherMode + setUserProfile');
      setTeacherMode(newTeacherMode);

      // Charger le profil de l'élève
      setUserProfile(studentData);
      console.log('[Auth] playWithStudent → done');

    } catch (error) {
      console.error('[Auth] Erreur activation mode cours:', error);
      throw error;
    }
  };

  // Désactiver le mode "Jouer avec élève" et revenir au profil professeur
  const exitStudentMode = () => {
    if (teacherMode.isActive && teacherMode.originalTeacherProfile) {
      // Basculer le localStorage vers le prof
      setCurrentUserId(teacherMode.originalTeacherId);

      // Restaurer le profil du professeur
      setUserProfile(teacherMode.originalTeacherProfile);

      // Réinitialiser le mode
      const emptyMode = {
        isActive: false,
        originalTeacherId: null,
        originalTeacherProfile: null,
        studentId: null,
        studentProfile: null
      };

      setTeacherMode(emptyMode);

      // Supprimer de sessionStorage (ADR-020)
      sessionStorage.removeItem('teacherMode');

    }
  };

  /**
   * Retourne l'UID effectif à utiliser pour sauvegarder les données
   * - Si en mode cours (professeur joue avec élève) : retourne l'UID de l'élève
   * - Sinon : retourne l'UID de l'utilisateur connecté
   */
  const getEffectiveUserId = () => {
    if (teacherMode.isActive && teacherMode.studentId) {
      return teacherMode.studentId; // UID de l'élève
    }
    return currentUser?.uid; // UID du professeur ou utilisateur normal
  };

  const value = {
    currentUser,
    userProfile,
    isFirstLogin,
    isPendingEmailVerification,
    loading,
    login,
    loginAnonymously,
    logout,
    createProfile,
    updatePseudo,
    markChallengeBadgeSeen,
    updatePassword,
    loadUserProfile,
    deleteAccount,
    resendVerificationEmail,
    refreshEmailVerification,
    teacherMode,
    playWithStudent,
    exitStudentMode,
    getEffectiveUserId
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
